import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/Textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, X } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { supabase } from "@/lib/supabase";
import { updateInventoryPendingOrders } from "@/lib/api/inventory";

export function CancellationDialog({ 
  isOpen, 
  onClose, 
  order, 
  onOrderCancelled 
}) {
  const [cancellationReason, setCancellationReason] = useState("");
  const [cancellationNotes, setCancellationNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const buildInventoryKey = (productId, size, color) => `${String(productId)}::${String(size)}::${String(color)}`;
  const isPartiallyServedOrder = order?.status === "partially_served";

  useEffect(() => {
    if (!isOpen) return;
    setCancellationNotes("");
    setCancellationReason(isPartiallyServedOrder ? "partial_served_not_manage" : "");
  }, [isOpen, isPartiallyServedOrder]);
  const copyTextToClipboard = async (text) => {
    if (!text) return false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_) {
      // Fallback below.
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textarea);
      return copied;
    } catch (_) {
      return false;
    }
  };

  const handleCancel = () => {
    setCancellationReason("");
    setCancellationNotes("");
    onClose();
  };

  const handleConfirmCancellation = async () => {
    if (!cancellationReason) {
      toast({
        title: "Error",
        description: "Debe seleccionar un motivo de cancelación",
        variant: "destructive",
      });
      return;
    }

    if (isPartiallyServedOrder && cancellationReason !== "partial_served_not_manage") {
      toast({
        title: "Motivo no permitido",
        description: "En pedidos parcialmente servidos solo se permite: Parcialmente servido y no se va a gestionar.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Obtener el usuario actual
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user?.id) throw new Error("No hay usuario autenticado para realizar la cancelación.");

      // Obtener los datos completos del pedido antes de eliminarlo
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select(`
          *,
          items:order_items(*)
        `)
        .eq('id', order.id)
        .single();

      if (orderError) throw orderError;

      // Protección ante reintentos si la cancelación previa quedó a medias
      const { data: existingCancellation, error: existingCancellationError } = await supabase
        .from('cancelled_orders')
        .select('id')
        .eq('original_order_id', order.id)
        .limit(1)
        .maybeSingle();

      if (existingCancellationError) throw existingCancellationError;
      let cancelledOrderId = existingCancellation?.id || null;

      const normalizedItems = (orderData.items || []).map((item) => {
        const quantity = Number(item.quantity) || 0;
        const deliveredQuantity = Number(item.delivered_quantity) || 0;
        const rawPending = Number(item.pending_quantity);
        const normalizedPending = Number.isFinite(rawPending)
          ? Math.max(0, rawPending)
          : Math.max(0, quantity - deliveredQuantity);

        return {
          ...item,
          quantity,
          delivered_quantity: deliveredQuantity,
          normalized_pending: normalizedPending,
        };
      });

      const hasServedLines = normalizedItems.some((item) => item.delivered_quantity > 0);
      if (hasServedLines && cancellationReason !== "partial_served_not_manage") {
        throw new Error("Este pedido tiene líneas servidas. Solo puede cancelarse con el motivo: Parcialmente servido y no se va a gestionar.");
      }

      const targetOrderStatus = "partially_served_cancelled";

      if (!cancelledOrderId) {
        // Crear el registro en cancelled_orders
        const { data: cancelledOrder, error: cancelledOrderError } = await supabase
          .from('cancelled_orders')
          .insert({
            original_order_id: order.id,
            customer_id: orderData.customer_id,
            date: orderData.date,
            total: orderData.total,
            status: targetOrderStatus,
            created_at: orderData.created_at,
            user_id: orderData.user_id,
            number: orderData.number,
            notes: orderData.notes,
            signature: orderData.signature,
            fecha_entrega: orderData.fecha_entrega,
            direccion_envio: orderData.direccion_envio,
            forma_pago: orderData.forma_pago,
            shipping_address_id: orderData.shipping_address_id,
            updated_at: orderData.updated_at,
            cod_rep: orderData.cod_rep,
            confirmacion_90dias: orderData.confirmacion_90dias,
            preparation_status: orderData.preparation_status,
            preparation_date: orderData.preparation_date,
            preparation_user_id: orderData.preparation_user_id,
            discount_percentage: orderData.discount_percentage,
            payment_transfer: orderData.payment_transfer,
            cancelled_at: new Date().toISOString(),
            cancelled_by: user.id,
            cancellation_reason: cancellationReason,
            cancellation_notes: cancellationNotes
          })
          .select()
          .single();

        if (cancelledOrderError) throw cancelledOrderError;
        cancelledOrderId = cancelledOrder.id;

        // Crear los registros en cancelled_order_items
        if (normalizedItems.length > 0) {
          // Guardar solo líneas realmente canceladas (pendientes > 0).
          const cancelledItems = normalizedItems
            .filter((item) => item.normalized_pending > 0)
            .map((item) => ({
              cancelled_order_id: cancelledOrderId,
              original_order_item_id: item.id,
              order_id: item.order_id,
              product_id: item.product_id,
              size: item.size || "N/A",
              color: item.color || "N/A",
              quantity: item.normalized_pending,
              pending_quantity: item.normalized_pending,
              price: Number(item.price) || 0,
              created_at: item.created_at || new Date().toISOString(),
              delivered_quantity: item.delivered_quantity,
              preparation_status: item.preparation_status || 'pending',
              preparation_quantity: Number(item.preparation_quantity) || 0,
              preparation_date: item.preparation_date,
              preparation_user_id: item.preparation_user_id
            }));

          if (cancelledItems.length > 0) {
            const { error: cancelledItemsError } = await supabase
              .from('cancelled_order_items')
              .insert(cancelledItems);

            if (cancelledItemsError) throw cancelledItemsError;
          }
        }
      }

      // Si ya hay líneas servidas, conservar trazabilidad con cancelación lógica.
      // Si no hay líneas servidas, se intenta borrado físico y se cae a lógica segura si hay FKs.
      let usedSoftCancellation = false;
      const applySoftCancellation = async () => {
        const { error: updateItemsError } = await supabase
          .from('order_items')
          .update({ pending_quantity: 0 })
          .eq('order_id', order.id)
          .gt('pending_quantity', 0);
        if (updateItemsError) throw updateItemsError;

        const { error: updateOrderError } = await supabase
          .from('orders')
          .update({
            status: targetOrderStatus,
            updated_at: new Date().toISOString()
          })
          .eq('id', order.id);
        if (updateOrderError) throw updateOrderError;
      };

      if (hasServedLines) {
        usedSoftCancellation = true;
        await applySoftCancellation();
      } else {
        try {
          const { error: itemsError } = await supabase
            .from('order_items')
            .delete()
            .eq('order_id', order.id);
          if (itemsError) throw itemsError;

          const { error: orderDeleteError } = await supabase
            .from('orders')
            .delete()
            .eq('id', order.id);
          if (orderDeleteError) throw orderDeleteError;
        } catch (deleteError) {
          if (deleteError?.code !== "23503") {
            throw deleteError;
          }
          usedSoftCancellation = true;
          await applySoftCancellation();
        }
      }

      // Reconciliar pending_orders de inventario contra líneas activas (idempotente y seguro para cancelaciones parciales previas)
      const affectedKeys = new Map();
      for (const item of (orderData.items || [])) {
        if (!item.product_id || !item.size || !item.color) continue;
        affectedKeys.set(
          buildInventoryKey(item.product_id, item.size, item.color),
          { product_id: item.product_id, size: item.size, color: item.color }
        );
      }

      const affectedCombinations = Array.from(affectedKeys.values());

      if (affectedCombinations.length > 0) {
        const affectedProductIds = [...new Set(affectedCombinations.map((c) => c.product_id))];
        const { data: remainingOrderItems, error: remainingOrderItemsError } = await supabase
          .from('order_items')
          .select('product_id, size, color, pending_quantity')
          .in('product_id', affectedProductIds)
          .gt('pending_quantity', 0);

        if (remainingOrderItemsError) throw remainingOrderItemsError;

        const remainingMap = new Map();
        for (const row of (remainingOrderItems || [])) {
          const key = buildInventoryKey(row.product_id, row.size, row.color);
          const current = remainingMap.get(key) || 0;
          remainingMap.set(key, current + (Number(row.pending_quantity) || 0));
        }

        const replaceUpdates = affectedCombinations.map((combination) => {
          const key = buildInventoryKey(combination.product_id, combination.size, combination.color);
          return {
            ...combination,
            pending_orders: remainingMap.get(key) || 0
          };
        });

        await updateInventoryPendingOrders(replaceUpdates, 'replace');
      }

      toast({
        title: "Pedido cancelado",
        description: usedSoftCancellation
          ? `El pedido #${order.number} conserva líneas servidas y se han anulado solo las pendientes.`
          : existingCancellation?.id
            ? `El pedido #${order.number} ya estaba en historial y se ha finalizado su limpieza.`
            : `El pedido #${order.number} ha sido cancelado y guardado en el historial`,
        duration: 3000,
      });

      // Llamar a la función de callback para actualizar la lista
      if (onOrderCancelled) {
        onOrderCancelled();
      }

      handleCancel();

    } catch (error) {
      console.error('Error cancelling order:', error);
      const messageParts = [
        error?.message,
        error?.details,
        error?.hint
      ].filter(Boolean);
      const errorText = messageParts[0]
        ? `No se pudo cancelar el pedido. ${messageParts.join(" | ")}`
        : "No se pudo cancelar el pedido. Inténtelo de nuevo.";
      toast({
        title: "Error",
        description: errorText,
        variant: "destructive",
        duration: 2147483647,
        autoDismiss: false,
        action: (
          <ToastAction
            altText="Copiar error"
            onClick={async () => {
              const copied = await copyTextToClipboard(errorText);
              toast({
                title: copied ? "Error copiado" : "No se pudo copiar",
                description: copied
                  ? "El detalle del error se ha copiado al portapapeles."
                  : "No se pudo copiar automáticamente. Puede copiar el texto del mensaje.",
                variant: copied ? "default" : "destructive",
              });
            }}
          >
            Copiar error
          </ToastAction>
        ),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getReasonLabel = (reason) => {
    const reasons = {
      'error': 'Error al introducir el pedido',
      'client_cancellation': 'Anulación expresa del cliente',
      'partial_served_not_manage': 'Parcialmente servido y no se va a gestionar'
    };
    return reasons[reason] || reason;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Cancelar Pedido #{order?.number}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">Atención:</p>
                <p>Esta acción conserva las líneas ya servidas y anula solo las líneas pendientes. El inventario actualizará únicamente lo pendiente y se guardará trazabilidad en historial.</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cancellation-reason">Motivo de cancelación *</Label>
            <Select
              value={cancellationReason}
              onValueChange={setCancellationReason}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar motivo" />
              </SelectTrigger>
              <SelectContent>
                {!isPartiallyServedOrder && (
                  <SelectItem value="error">Error al introducir el pedido</SelectItem>
                )}
                {!isPartiallyServedOrder && (
                  <SelectItem value="client_cancellation">Anulación expresa del cliente</SelectItem>
                )}
                <SelectItem value="partial_served_not_manage">Parcialmente servido y no se va a gestionar</SelectItem>
              </SelectContent>
            </Select>
            {isPartiallyServedOrder && (
              <p className="text-xs text-amber-700">
                Pedido parcialmente servido: el único motivo permitido es "Parcialmente servido y no se va a gestionar".
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="cancellation-notes">Notas adicionales (opcional)</Label>
            <Textarea
              id="cancellation-notes"
              placeholder="Describa detalles adicionales sobre la cancelación..."
              value={cancellationNotes}
              onChange={(e) => setCancellationNotes(e.target.value)}
              rows={3}
            />
          </div>

          {cancellationReason && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Motivo seleccionado:</span> {getReasonLabel(cancellationReason)}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmCancellation}
            disabled={!cancellationReason || isLoading}
            className="bg-red-600 hover:bg-red-700"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Cancelando...
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 mr-2" />
                Confirmar Cancelación
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 
